/**
 * The sea's motion, as arithmetic — no three.js here, so every number below is a
 * plain unit test (`ocean-waves.test.ts`). `three/ocean-material.ts` is the
 * shader that evaluates it; `ocean-look.ts` is what colour the result is
 * painted. The same split `enhanced-sky-look.ts` and `enhanced-sky.ts` use, for
 * the same reason: a wavelength is a number someone can check against the screen
 * and a shader is not.
 *
 * ## Why a sum of directional waves and not a noise field
 *
 * `three/cloud-noise.ts` makes cloud out of value noise on a lattice, which is
 * right for cloud and wrong here for one measured reason: **a lattice repeats.**
 * The primary viewing condition for this feature is a cockpit at patrol height
 * looking down at open water, where the same square kilometre is on screen a
 * hundred times over, and a hashed lattice at that scale reads as a printed
 * tablecloth.
 *
 * A sum of plane waves has no lattice to repeat on. It repeats exactly only if
 * some non-zero displacement `L` satisfies `dot(L, k_j) ∈ 2πZ` for **every**
 * wavevector `k_j` at once — and the train below is built so that no such `L`
 * exists:
 *
 * - the wavelengths are a geometric ladder in `φ = 1.618…` (`WAVELENGTH_RATIO`),
 *   so no two of them are in a rational ratio;
 * - the bearings step by the golden angle `180(3 − √5) = 137.5077…°`
 *   (`BEARING_STEP_DEG`), so no two directions are in a rational ratio either.
 *
 * Both are irrational by construction rather than by choice of decimal, which is
 * why they are written as expressions in `√5` and not as literals.
 *
 * ## Quasi-periodic is not the same as "never looks similar", and the count of
 * octaves is what settles it
 *
 * A field like this is *almost*-periodic: there is no exact repeat, but there
 * are lags at which every octave happens to come back near its own phase at
 * once, and the more octaves there are the rarer those get — the chance of an
 * `n`-fold coincidence falls like `ε^n`. That is measurable and
 * `ocean-waves.test.ts` measures it, against a **commensurate control** (the
 * same eight octaves on a ×2 ladder and axis-aligned bearings, which is what an
 * ordinary fBm arrangement amounts to) so the number has something to be read
 * against.
 *
 * It is also what set `OCEAN_OCTAVES` at eight rather than the five this was
 * first built with. A five-octave train on a `φ²` ladder is fine at sea level
 * and poor in exactly the condition the feature exists for: at patrol height
 * only the **three** longest octaves are still wider than a pixel
 * (`three/ocean-material.ts` filters the rest), and three octaves recur well.
 * Measured over ±2 km at a 4 m lag step, the resolved field:
 *
 * | train | octaves resolved at 6 000 ft | best recurrence | lags above 0.99 |
 * |---|---|---|---|
 * | five on `φ²` | 3 | **0.9995** | **238** of 1 001 932 |
 * | eight on `φ` (shipped) | 6 | 0.9747 | **0** |
 * | commensurate control (×2, axis-aligned) | 8 | **1.0000** exactly | 24 |
 *
 * — and for the whole train, 0.9881 against the shipped 0.9492. The control row
 * is the arrangement an ordinary fBm stack amounts to and it is in
 * `ocean-waves.test.ts` beside the real one, because "this does not repeat" only
 * means something next to something that does.
 *
 * ## Equal steepness per octave
 *
 * Each octave carries the same **steepness** `A·k = OCTAVE_STEEPNESS`, not the
 * same amplitude, so the amplitude falls with the wavelength exactly as fast as
 * the wavenumber rises. That has two properties worth having: the octaves
 * contribute equally to the surface *slope*, which is the only quantity the
 * shading and the glitter read; and the total slope is bounded by
 * `OCTAVE_STEEPNESS × OCEAN_OCTAVES` by construction, so the sea cannot break
 * into overhangs no matter what the constants are set to. At the shipped values
 * that bound is 0.224, i.e. **12.6° of maximum face slope** — a moderate sea,
 * and the number `ocean-look.ts`'s two-step shade swing is sized against. It is
 * also what made the octave count above a free choice: adding octaves
 * *subdivides* the slope budget rather than roughening the water, so eight
 * versus five was decided on recurrence and frame cost alone and the sea looks
 * the same either way from a distance.
 *
 * ## Deep-water dispersion, so the long swell outruns the ripples
 *
 * `c = √(gλ/2π)` is the deep-water gravity-wave phase speed, which is the one
 * piece of real physics in this file and the reason the picture reads as sea
 * rather than as a moving texture: the 193 m swell travels at 17.4 m/s and the
 * 6.6 m chop at 3.2 m/s, so the two slide through each other. A single scroll
 * speed for every octave was tried first and it looks like a sheet of paper
 * being pulled.
 *
 * ## Why the phases are computed here and not in the shader
 *
 * A region is 600 km across and the finest octave is 6.6 m, so
 * `dot(direction, worldXZ) / wavelength` at the far corner of London's sea plate
 * is about 5.6 × 10⁴ **cycles**, and a float32 cannot hold a cycle count that
 * large with any of the fraction left over — the phase is the fractional part
 * and that is precisely what gets rounded away. Shaders that do this show it as
 * the wave field going smooth and then blocky as you fly away from the origin.
 *
 * So the shader never evaluates an absolute position. `oceanWavePhases` reduces
 * each octave's phase at an **anchor** point (the player's own aeroplane) modulo
 * one cycle here in float64, and the shader adds only
 * `dot(direction, fragment − anchor) / wavelength`, whose magnitude is bounded by
 * the far plane. The anchor is passed as a float32 uniform and is rounded to
 * float32 *before* the phases are taken off it (`anchorToFloat32`), so the two
 * halves are computed from the same number and the field is continuous across a
 * frame in which the anchor moved.
 */

/** Standard gravity, m/s² — the only physical constant the wave train needs. */
export const GRAVITY_MPS2 = 9.80665;

/**
 * How many plane waves are summed. **Eight**, and the number was measured
 * rather than picked — see the header's second section: at five, six of the
 * eight are still resolved at patrol height where three of five were, and the
 * recurrence scan over the drawn world goes from 960 near-repeats to none.
 * The floor of the ladder is 6.6 m, which is fine enough for a low pass, and
 * a ninth octave buys a further 0.05 of recurrence for a 4 m ripple nobody
 * resolves above the mast.
 */
export const OCEAN_OCTAVES = 8;

/**
 * The longest wave, in metres. A 193 m swell is a moderate open-Channel sea, and
 * it is the octave that survives to patrol height: at 6 000 ft it subtends about
 * 1.8° looking straight down, i.e. some 20 px of a 640-line view, so the sea has
 * visible structure from the cockpit rather than being a flat plate with glitter
 * on it.
 */
export const SWELL_WAVELENGTH_M = 193;

/** `φ`, the ladder each octave's wavelength divides by. Irrational, which is the whole point — see the header. */
export const WAVELENGTH_RATIO = (1 + Math.sqrt(5)) / 2;

/** The golden angle in degrees, `180(3 − √5)` — the bearing step, irrational for the same reason. */
export const BEARING_STEP_DEG = 180 * (3 - Math.sqrt(5));

/**
 * The bearing the primary swell runs on, degrees clockwise from world `−Z`.
 *
 * **INVENTED, and deliberately not read from the sim's own wind.** The manual's
 * prevailing wind is a westerly and `packages/engine`'s `windDirectionRad` is
 * nevertheless **0** — that constant's own doc says why: the front line in this
 * port is a plane of constant `z`, so heading 0 is what every briefing means by
 * "over the wire", and the compass rose was traded for the manual's *mechanic*
 * on purpose. Taking the swell off that number would put the sea running due
 * north for a reason that has nothing to do with weather. 250° is the compass
 * answer to the compass question — the west-south-westerly set the Channel
 * actually carries — and nothing in the sim reads it.
 */
export const SWELL_BEARING_DEG = 250;

/**
 * `A·k` for every octave — the header's equal-steepness rule, as one number.
 * 0.028 × 8 octaves is a 0.224 bound on the total slope, 12.6°: the same sea
 * the five-octave version made, cut into more pieces.
 */
export const OCTAVE_STEEPNESS = 0.028;

/** One plane wave of the train. */
export interface OceanWave {
  /** Metres from crest to crest. */
  readonly wavelengthM: number;
  /** Crest-to-trough over two, metres — `OCTAVE_STEEPNESS · λ / 2π` by construction. */
  readonly amplitudeM: number;
  /** Unit direction of travel in world XZ. */
  readonly directionX: number;
  readonly directionZ: number;
  /** Deep-water phase speed, m/s. */
  readonly speedMps: number;
}

/** `c = √(gλ/2π)`, the deep-water gravity-wave phase speed. */
export function waveSpeedMps(wavelengthM: number): number {
  return Math.sqrt((GRAVITY_MPS2 * wavelengthM) / (2 * Math.PI));
}

/**
 * The train, derived from the five constants above rather than tabulated — a
 * table would be a second place for the ladder to be wrong, and
 * `ocean-waves.test.ts` re-derives the same numbers from the same expressions.
 */
export const OCEAN_WAVES: readonly OceanWave[] = Array.from({ length: OCEAN_OCTAVES }, (_, octave) => {
  const wavelengthM = SWELL_WAVELENGTH_M / WAVELENGTH_RATIO ** octave;
  const bearingRad = ((SWELL_BEARING_DEG + BEARING_STEP_DEG * octave) * Math.PI) / 180;
  return {
    wavelengthM,
    amplitudeM: (OCTAVE_STEEPNESS * wavelengthM) / (2 * Math.PI),
    // Bearing 0 is world `−Z` and bearing grows clockwise seen from above, which
    // is `map/map-model.ts`'s convention and the one the region's own headings
    // use.
    directionX: Math.sin(bearingRad),
    directionZ: -Math.cos(bearingRad),
    speedMps: waveSpeedMps(wavelengthM),
  };
});

/** The bound the header argues for: the largest surface slope the train can produce, as a gradient magnitude. */
export function maxWaveSlope(): number {
  return OCEAN_WAVES.reduce((total, wave) => total + (2 * Math.PI * wave.amplitudeM) / wave.wavelengthM, 0);
}

/**
 * Rounds an anchor coordinate the way the GPU will see it.
 *
 * Load-bearing, not fastidious: the phases below are subtracted at the anchor
 * and the shader adds `fragment − anchor` back at float32. If the CPU takes the
 * phase off a float64 anchor and the GPU subtracts a float32 one, the two
 * disagree by up to half an ulp of the anchor — 0.03 m at the far corner of a
 * sea plate — which on a 10.8 m ripple is a third of a degree of phase that
 * changes every frame. That is a shimmer, and this is the line that removes it.
 */
export function anchorToFloat32(metres: number): number {
  return Math.fround(metres);
}

/**
 * Each octave's phase at `(anchorX, anchorZ)` and time `timeS`, in **cycles**,
 * reduced to `[0, 1)`.
 *
 * `timeS` is the encounter's own clock (`EncounterState.timeS`), never
 * `performance.now()`: a paused sortie has a frozen sea, the kill camera's
 * `dt = 0` re-draw gets the same sea as the pass before it, and
 * `apps/web/scripts/pose-sweep.ts` can still photograph two builds of the same
 * frozen pose and compare them byte for byte.
 */
export function oceanWavePhases(anchorX: number, anchorZ: number, timeS: number): number[] {
  const x = anchorToFloat32(anchorX);
  const z = anchorToFloat32(anchorZ);
  return OCEAN_WAVES.map((wave) => {
    const cycles = (wave.directionX * x + wave.directionZ * z - wave.speedMps * timeS) / wave.wavelengthM;
    return cycles - Math.floor(cycles);
  });
}

/**
 * The surface gradient `(∂h/∂x, ∂h/∂z)` at a point offset from the anchor the
 * phases were taken at — the scalar twin of what the shader computes, so a test
 * can measure the field's autocorrelation and its slope bound without a GPU.
 */
export function oceanSlopeAt(offsetX: number, offsetZ: number, phases: readonly number[]): [number, number] {
  let slopeX = 0;
  let slopeZ = 0;
  OCEAN_WAVES.forEach((wave, octave) => {
    const cycles = (wave.directionX * offsetX + wave.directionZ * offsetZ) / wave.wavelengthM + (phases[octave] ?? 0);
    const wavenumber = (2 * Math.PI) / wave.wavelengthM;
    const term = wave.amplitudeM * wavenumber * Math.cos(2 * Math.PI * cycles);
    slopeX += term * wave.directionX;
    slopeZ += term * wave.directionZ;
  });
  return [slopeX, slopeZ];
}

/** The surface height at an offset from the anchor, metres — what the autocorrelation test correlates. */
export function oceanHeightAt(offsetX: number, offsetZ: number, phases: readonly number[]): number {
  let height = 0;
  OCEAN_WAVES.forEach((wave, octave) => {
    const cycles = (wave.directionX * offsetX + wave.directionZ * offsetZ) / wave.wavelengthM + (phases[octave] ?? 0);
    height += wave.amplitudeM * Math.sin(2 * Math.PI * cycles);
  });
  return height;
}
